import React from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import { Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router';

import { hasSupervisorFeatureFlag } from '@paragon/login-tools';
import { sortBy } from '@paragon/utilities';

import { showCreateDirectoryDialog, showCreateLineDialog, showEditDirectoryDialog } from '../../actions/dialogActions';
import { deleteDirectory } from '../../actions/supervisorActions';
import { useDirectoryBreadcrumbs } from '../../selectors/breadcrumbsSelector';
import { DirectoryViewModel, getHolisticViewModel } from '../../selectors/holisticViewModelSelector';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Breadcrumbs } from '../interface/Breadcrumbs';
import { HierarchicalViewModelCard } from '../interface/cards/HierarchicalViewModelCard';
import { ResponsiveButtonBar } from '../interface/ResponsiveButtonBar';
import { ErrorPage } from './ErrorPage';

export function DirectoryPage() {
  const { directoryGuid } = useParams();
  const dispatch = useAppDispatch();

  const breadcrumbs = useDirectoryBreadcrumbs(directoryGuid!);
  const isSupervisor = useAppSelector(hasSupervisorFeatureFlag);

  const viewModel = useAppSelector(getHolisticViewModel);
  if (viewModel === 'loading' || viewModel === 'error') return null;

  const directoryViewModel = viewModel.flatView[directoryGuid!] as DirectoryViewModel;
  if (directoryViewModel == null) return <ErrorPage body="That directory can not be found." />;

  const directories = sortBy(
    Object.entries(directoryViewModel.children.subdirectories),
    ([_, model]) => model.source.name,
  );
  const lines = sortBy(Object.entries(directoryViewModel.children.lines), ([_, model]) => model.source.name);

  const directoryCards = directories.map(([guid, model]) => (
    <Grid size={{ xs: 12, md: 6 }} key={guid}>
      <HierarchicalViewModelCard model={model} />
    </Grid>
  ));

  const lineCards = lines.map(([guid, model]) => (
    <Grid size={{ xs: 12, md: 6 }} key={guid}>
      <HierarchicalViewModelCard model={model} />
    </Grid>
  ));

  return (
    <Stack>
      {breadcrumbs.length > 1 && <Breadcrumbs crumbs={breadcrumbs} />}

      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ marginRight: 'auto' }}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <FolderIcon fontSize="inherit" sx={{ marginRight: 1 }} />
            {directoryViewModel.source.name}
          </Stack>
        </Typography>

        {isSupervisor && (
          <ResponsiveButtonBar
            breakpoint="md"
            actions={[
              ['Edit', () => void dispatch(showEditDirectoryDialog(directoryGuid!))],
              ['Delete', () => void dispatch(deleteDirectory(directoryGuid!))],
            ]}
          />
        )}
      </Stack>

      {isSupervisor && (
        <Grid container spacing={1} sx={{ marginBottom: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => void dispatch(showCreateDirectoryDialog(directoryGuid!))}
            >
              Create new directory
            </Button>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Button fullWidth variant="outlined" onClick={() => void dispatch(showCreateLineDialog(directoryGuid!))}>
              Create new line
            </Button>
          </Grid>
        </Grid>
      )}

      <Grid container sx={{ alignItems: 'stretch' }} spacing={1}>
        {directoryCards}
      </Grid>

      {directoryCards.length > 0 && <Divider sx={{ marginY: 2 }} />}

      <Grid container sx={{ alignItems: 'stretch' }} spacing={1}>
        {lineCards}
      </Grid>
    </Stack>
  );
}
